// @ts-nocheck
import { useEffect, useState } from "react";
import {
  Images,
  LayoutGrid,
  Loader2,
  MessageSquare,
  Palette,
  StickyNote,
} from "lucide-react";
import { STUDIO_TIPS } from "@/lib/carouselStudioMeta";
import CopyJobInline from "@/components/CopyJobInline";
import { CopyReviewChanges } from "@/components/CopyReviewChanges";
import CopyChatPanel from "@/components/CopyChatPanel";
import { AiActionButton, CopyOutcomePanel, StudioSplit } from "@/components/studioTabs";
import { copyRenderBlockedReason } from "@/lib/studioVoice";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TabsContent } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { OP_SURFACE } from "@/lib/opSurfaces";
import Slide1PhotoProposals from "@/components/Slide1PhotoProposals";

import * as Helpers from "../helpers";
const {
  ideaDescription,
  isHeadlineSlide,
  slideHaslo,
  slideOpis,
  slideHasCopy,
  previewMarks,
  CopyMarkdownCheat,
  CopyMarkdownField,
  flattenSlide,
  Chip,
  FieldLabel,
} = Helpers;

export default function GeneratorTab({ ctx }: { ctx: any }) {
  const {
    abortLlmM,
    aiAbort,
    aiError,
    aiFailed,
    aiJob,
    aiJobParams,
    aiStatusLabel,
    applyCopyReviewM,
    bgMode,
    catalog,
    catalogTypes,
    clickAi,
    commitCopyField,
    commitCopyNumbered,
    copyBusy,
    copyChatApplyM,
    copyChatDismissM,
    copyChatM,
    copyChatUndoM,
    copyFeedback,
    copyMdRef,
    copyReviewOnDemandM,
    copyReviseRunning,
    copyRunning,
    copySlides,
    generateCopyM,
    goTab,
    hasComposedSlides,
    hasCopySlides,
    hasSelectedIdea,
    isAiTrack,
    isOperator,
    item,
    renderM,
    renderRunning,
    selectedId,
    selectedIdea,
    setCopyFeedback,
    studio,
    syncSlideCopyM,
    templateM,
    visualPackId,
  } = ctx;

  const [activeSlide, setActiveSlide] = useState(0);
  const chatMessages = Array.isArray(item?.discussion?.copy_chat) ? item.discussion.copy_chat : [];
  const canUndoChat = Boolean(item?.discussion?.copy_chat_undo?.copy);
  const pendingActions = Array.isArray(item?.discussion?.copy_chat_pending?.actions)
    ? item.discussion.copy_chat_pending.actions
    : [];
  const chatBusy =
    copyChatM?.isPending ||
    item?.llm_status === "copy_chat" ||
    copyReviewOnDemandM?.isPending ||
    item?.llm_status === "review" ||
    copyChatApplyM?.isPending ||
    copyChatDismissM?.isPending;

  const copyJobActive =
    Boolean(copyRunning || copyReviseRunning) ||
    aiJob === "copy" ||
    aiJob === "copyRevise" ||
    ["discuss", "copy", "review", "error"].includes(String(item?.llm_status || ""));

  useEffect(() => {
    if (!copySlides?.length) {
      setActiveSlide(0);
      return;
    }
    if (activeSlide >= copySlides.length) setActiveSlide(copySlides.length - 1);
  }, [copySlides?.length, activeSlide]);

  return (
    <TabsContent value="generator" className="mt-0 space-y-5">
      {copyJobActive ? (
        <CopyJobInline
          kind={aiJob === "copyRevise" ? "copyRevise" : "copy"}
          item={item}
          params={typeof aiJobParams === "function" ? aiJobParams() : []}
          running={Boolean(copyRunning || copyReviseRunning)}
          failed={Boolean(aiFailed && (aiJob === "copy" || aiJob === "copyRevise" || item?.llm_status === "error"))}
          statusLabel={
            aiJob === "copy" || aiJob === "copyRevise" || copyRunning || copyReviseRunning
              ? aiStatusLabel?.()
              : undefined
          }
          error={
            aiJob === "copy" || aiJob === "copyRevise" || copyRunning || copyReviseRunning || item?.llm_status === "error"
              ? aiError
              : undefined
          }
          abortPending={abortLlmM.isPending}
          onAbort={aiAbort}
          copyReviewPending={applyCopyReviewM?.isPending}
          copyReviewError={applyCopyReviewM?.isError ? (applyCopyReviewM.error as Error)?.message : ""}
          onApplyCopyReview={(actionIds, rejectIds) =>
            applyCopyReviewM.mutate({ actionIds, rejectIds })
          }
        />
      ) : null}

      {!hasSelectedIdea && !hasCopySlides && !isOperator ? (
        <Card className="border-slate-200 bg-white shadow-sm">
          <CardContent className="space-y-3 pt-6">
            <p className="text-sm text-muted-foreground">
              Najpierw wybierz kartę koncepcji. Typ i styl ustawisz tutaj, potem Generuj treść.
            </p>
            <Button type="button" variant="outline" onClick={() => goTab("copy")}>
              Wróć do koncepcji
            </Button>
          </CardContent>
        </Card>
      ) : null}

      <StudioSplit
        left={
          <div className="space-y-5">
            {(item?.layout_plan?.slides?.length ||
              item?.llm_status === "layout_plan") && (
              <Card id="layout-plan" className="border-slate-200 bg-white shadow-sm">
                <CardHeader>
                  <CardTitle className="text-base text-slate-900">Plan układu</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {item?.llm_status === "layout_plan" ? (
                    <p className="text-sm text-muted-foreground">{item?.llm_detail || "Dobieram rytm ścian…"}</p>
                  ) : null}
                  {item?.layout_plan?.rationale ? (
                    <p className="text-sm text-foreground">{item.layout_plan.rationale}</p>
                  ) : null}
                  {item?.layout_plan?.format_id ? (
                    <p className="text-xs text-muted-foreground">
                      Typ: {item.layout_plan.format_id}
                      {item.layout_plan.pattern ? ` · wzorzec ${item.layout_plan.pattern}` : ""}
                    </p>
                  ) : null}
                  <div className={cn("grid gap-2 p-3 md:grid-cols-2", OP_SURFACE.result.box)}>
                    {(item?.layout_plan?.slides || []).map((row) => (
                      <div key={`${row.index}-${row.recipe}`} className="rounded border border-border/60 bg-muted/20 p-2 text-xs">
                        <div className="font-medium">
                          Slajd {row.index}: {row.recipe}
                        </div>
                        {row.why ? <p className="mt-1 text-muted-foreground">{row.why}</p> : null}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <Card id="copy" className="border-slate-200 bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="text-base text-slate-900">
                  Copy slajdów
                  {selectedIdea?.hook ? (
                    <span className="mt-1 block text-xs font-normal text-muted-foreground">
                      Karta: {selectedIdea.hook}
                    </span>
                  ) : null}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {item && (
                  <div className="grid gap-4 xl:grid-cols-2">
                    <div>
                      <FieldLabel tip={STUDIO_TIPS.copyType} icon={LayoutGrid} tone="primary">
                        Typ tej karuzeli
                      </FieldLabel>
                      <div className="flex flex-wrap gap-1.5">
                        <Chip
                          active={!item.format_id}
                          onClick={() => templateM.mutate({ format_id: "" })}
                          tip={STUDIO_TIPS.mix}
                          tone="primary"
                        >
                          mieszanka
                        </Chip>
                        {catalogTypes.map((fmt) => (
                          <Chip
                            key={fmt.id}
                            active={item.format_id === fmt.id}
                            onClick={() => templateM.mutate({ format_id: fmt.id })}
                            tip={fmt.hint || fmt.label}
                            tone="primary"
                          >
                            {fmt.label}
                          </Chip>
                        ))}
                      </div>
                      {item.status === "draft" && !hasCopySlides && !copyBusy && (
                        <p className="mt-1 text-[11px] text-muted-foreground">
                          {isOperator
                            ? "Typ i styl zapiszą się do tej karuzeli. Tekst startuje po Zredaguj slajdy."
                            : "Typ i styl zapiszą się do tej karuzeli. Potem kliknij Generuj treść."}
                        </p>
                      )}
                    </div>
                    <div>
                      <FieldLabel tip={STUDIO_TIPS.visualPack} icon={Palette} tone="secondary">
                        Szablon wizualny
                      </FieldLabel>
                      <div className="mb-3 flex flex-wrap gap-2">
                        <Chip
                          active={!(item.visual_pack_id || visualPackId)}
                          onClick={() => templateM.mutate({ visual_pack_id: "" })}
                          tip="Bez paczki rytmu"
                        >
                          Bez paczki
                        </Chip>
                        {(catalog?.visual_packs || []).map((pack) => (
                          <Chip
                            key={pack.id}
                            active={(item.visual_pack_id || visualPackId) === pack.id}
                            onClick={() => templateM.mutate({ visual_pack_id: pack.id })}
                            tip={pack.hint || pack.label}
                          >
                            {pack.label}
                          </Chip>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                {item?.quality_flags && item.quality_flags.length > 0 && (
                  <div className="text-xs text-destructive">Flagi: {item.quality_flags.join(", ")}</div>
                )}
                {!hasCopySlides && (
                  <div className="space-y-2">
                    {selectedIdea && ideaDescription(selectedIdea) ? (
                      <p className="rounded-md border border-border bg-muted/30 p-3 text-sm">
                        {ideaDescription(selectedIdea)}
                      </p>
                    ) : null}
                    <p className="text-sm text-muted-foreground">
                      {copyBusy
                        ? item?.llm_detail ||
                          (isOperator ? "Wstawiam szkic na ściany…" : "Role rozmawiają, redaktor składa slajdy…")
                        : isOperator
                          ? "Wklej szkic i kliknij Zredaguj slajdy."
                          : hasSelectedIdea
                            ? "Koncepcja wybrana. Ustaw typ i styl, potem Generuj treść."
                            : "Wybierz koncepcję na Przetwarzaniu, potem wróć tutaj."}
                    </p>
                  </div>
                )}
                {isAiTrack && !hasCopySlides ? (
                  <div className="space-y-2">
                    <FieldLabel tip={STUDIO_TIPS.copyFeedback} icon={StickyNote} tone="muted">
                      Twoja uwaga do modelu
                    </FieldLabel>
                    <textarea
                      className="min-h-[72px] w-full rounded-md border border-input bg-transparent p-2 text-sm"
                      placeholder="za mało treści na kartach 2-4, rozwiń każdą do dwóch zdań…"
                      value={copyFeedback}
                      disabled={!item || !hasSelectedIdea || copyBusy}
                      onChange={(e) => setCopyFeedback(e.target.value)}
                    />
                  </div>
                ) : null}
                <div id="studio-primary-cta" className="flex flex-wrap items-start gap-3">
                  {hasCopySlides ? (
                    <>
                      <AiActionButton
                        className="w-auto"
                        label="Złóż obrazki"
                        io="render"
                        running={renderRunning}
                        failed={item?.render_status === "failed" || renderM.isError}
                        disabled={!item || copyBusy || renderRunning || Boolean(copyRenderBlockedReason(item))}
                        onClick={() => {
                          goTab("render");
                          clickAi("render", () =>
                            renderM.mutate((item?.bg_mode || bgMode || "flat") === "flat" || !studio?.gemini_ready),
                          );
                        }}
                        icon={<Images className="mr-2 h-3.5 w-3.5" />}
                      />
                      {copyRenderBlockedReason(item) ? (
                        <p className="w-full text-xs text-amber-800">
                          Najpierw wdróż poprawki z recenzji (albo popraw treść), potem złóż obrazki.
                        </p>
                      ) : null}
                    </>
                  ) : null}
                  {isAiTrack ? (
                    <AiActionButton
                      className="w-auto"
                      label={hasCopySlides ? "Generuj treść ponownie" : "Generuj treść"}
                      io="copy"
                      variant={hasCopySlides ? "outline" : "brand"}
                      running={copyRunning && !copyReviseRunning}
                      failed={generateCopyM.isError || item?.llm_status === "error"}
                      disabled={!item || !hasSelectedIdea}
                      onClick={() => clickAi("copy", () => generateCopyM.mutate())}
                      icon={<MessageSquare className="mr-2 h-3.5 w-3.5" />}
                    />
                  ) : null}
                </div>
                {hasCopySlides ? (
                  <p className="text-xs text-muted-foreground">{STUDIO_TIPS.copyChat}</p>
                ) : null}
              </CardContent>
            </Card>
          </div>
        }
        right={
          <div className="space-y-5">
            <CopyOutcomePanel item={item} selectedHook={selectedIdea?.hook} />
            {isAiTrack && !!item?.discussion?.panel?.length && !hasCopySlides ? (
              <Card className="border-slate-200 bg-white shadow-sm">
                <CardHeader>
                  <CardTitle className="text-base text-slate-900">Transkrypt ról</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {item.discussion.verdict ? <p className="text-xs">{item.discussion.verdict}</p> : null}
                  <div className="grid gap-2">
                    {item.discussion.panel.map((voice, i) => (
                      <div key={`${voice.role}-${i}`} className="rounded bg-muted/40 p-2 text-xs">
                        <div className="mb-1 font-medium">{voice.role}</div>
                        <p className="whitespace-pre-wrap text-muted-foreground">{voice.text}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ) : null}
            {item?.discussion?.copy_review ? (
              <Card className="border-slate-200 bg-white shadow-sm">
                <CardHeader>
                  <CardTitle className="flex flex-wrap items-center justify-between gap-2 text-base text-slate-900">
                    Recenzja copy
                    <Badge variant={item.discussion.copy_review.verdict === "ok" ? "success" : "outline"}>
                      {item.discussion.copy_review.verdict || "brak werdyktu"}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <p className="text-xs text-muted-foreground">{STUDIO_TIPS.copyReview}</p>
                  {item.discussion.copy_review.error ? (
                    <p className="text-xs text-destructive">{item.discussion.copy_review.error}</p>
                  ) : null}
                  {!hasCopySlides ? (
                    <div className="grid gap-2">
                      {(item.discussion.copy_review.panel || []).map((voice, i) => (
                        <div key={`review-${voice.role}-${i}`} className="rounded bg-muted/40 p-2 text-xs">
                          <div className="mb-1 font-medium">{voice.role}</div>
                          <p className="whitespace-pre-wrap text-muted-foreground">{voice.text}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Głosy ról są w czacie. Tu zostają tylko poprawki do wdrożenia.
                    </p>
                  )}
                  <CopyReviewChanges
                    actions={item.discussion.copy_review.actions || []}
                    reviewVerdict={item.discussion.copy_review.verdict || ""}
                    itemId={item.id}
                    disabled={copyBusy || !hasCopySlides}
                    pending={applyCopyReviewM.isPending}
                    error={applyCopyReviewM.isError ? (applyCopyReviewM.error as Error)?.message : ""}
                    onApply={(ids, rejectIds) => applyCopyReviewM.mutate({ actionIds: ids, rejectIds })}
                  />
                </CardContent>
              </Card>
            ) : (
              <p className="text-sm text-muted-foreground">
                {hasCopySlides
                  ? "Recenzja ról pojawi się po przycisku Review ról w czacie."
                  : "Tu pojawi się transkrypt i recenzja po generatorze treści."}
              </p>
            )}
          </div>
        }
      />

      {hasCopySlides ? (
        <div id="copy-slides" className="space-y-4">
          <Card className="w-full min-w-0 border-slate-200 bg-white shadow-sm">
            <CardContent className="space-y-3 pt-6">
              {item ? <Slide1PhotoProposals item={item} fallback={bgMode === "flat"} /> : null}
              <p className="text-[11px] text-muted-foreground">
                Wąskie slajdy w jednym rzędzie na całą szerokość. Kliknij nagłówek, żeby zaznaczyć (chat). Markdown
                ręcznie; Złóż bierze już zjedzony tekst.
              </p>
              <CopyMarkdownCheat />
              <div className="flex flex-wrap gap-1.5">
                {copySlides.map((slide, idx) => (
                  <button
                    key={`jump-${slide.type || "s"}-${idx}`}
                    type="button"
                    className={cn(
                      "rounded-md border px-2.5 py-1 text-xs",
                      activeSlide === idx
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-border bg-background text-muted-foreground hover:text-foreground",
                    )}
                    onClick={() => {
                      setActiveSlide(idx);
                      document
                        .getElementById(`copy-slide-${idx + 1}`)
                        ?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
                    }}
                  >
                    Slajd {idx + 1}
                  </button>
                ))}
              </div>
              <div className="w-full overflow-x-auto pb-1">
                <div
                  className="grid w-full min-w-[52rem] gap-2"
                  style={{
                    gridTemplateColumns: `repeat(${Math.max(copySlides.length, 1)}, minmax(0, 1fr))`,
                  }}
                >
                  {copySlides.map((slide, idx) => {
                    const source = slideHasCopy(slide)
                      ? slide
                      : (item?.copy?.slides?.[idx] as Record<string, unknown>) || slide;
                    const haslo = slideHaslo(source);
                    const opis = slideOpis(source);
                    const selected = activeSlide === idx;
                    return (
                      <div
                        key={`slide-block-${slide.type || "s"}-${idx}`}
                        id={`copy-slide-${idx + 1}`}
                        className={cn(
                          "flex min-w-0 flex-col rounded-md border p-2 text-left shadow-sm transition-shadow",
                          selected
                            ? "border-slate-900 bg-slate-100 shadow-[0_0_0_1px_rgba(15,23,42,0.35)]"
                            : "border-slate-200 bg-slate-50",
                        )}
                      >
                        <button
                          type="button"
                          className="mb-2 flex w-full items-center justify-between gap-1 text-left"
                          onClick={() => setActiveSlide(idx)}
                        >
                          <span className="truncate text-[10px] uppercase tracking-wide text-muted-foreground">
                            {idx + 1}. {String(source.type || "treść")}
                          </span>
                          <span
                            className={cn(
                              "shrink-0 text-[9px] font-medium uppercase tracking-wide",
                              selected ? "text-slate-700" : "text-muted-foreground/70",
                            )}
                          >
                            {selected ? "·" : "○"}
                          </span>
                        </button>
                        <label
                          className="mb-2 flex items-center gap-1.5 text-[10px] text-muted-foreground"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            className="h-3 w-3"
                            checked={Boolean(source.numbered)}
                            disabled={copyBusy || !selectedId}
                            onChange={(e) => commitCopyNumbered?.(idx, e.target.checked)}
                          />
                          Numeruj
                        </label>
                        {isHeadlineSlide(source) ? (
                          <div className="flex min-h-0 flex-1 flex-col gap-1.5">
                            <CopyMarkdownField
                              label="Hasło"
                              text={haslo}
                              marks={previewMarks(source, "text", haslo)}
                              bold
                              minHeightClass="min-h-[72px]"
                              disabled={copyBusy || !selectedId}
                              onDraft={(next) => {
                                copyMdRef.current[`${idx}:text`] = next;
                              }}
                              onCommit={(next) => commitCopyField(idx, "text", next)}
                            />
                            <CopyMarkdownField
                              label="Opis"
                              text={opis}
                              marks={previewMarks(source, "sub", opis)}
                              minHeightClass="min-h-[280px]"
                              disabled={copyBusy || !selectedId}
                              onDraft={(next) => {
                                copyMdRef.current[`${idx}:sub`] = next;
                              }}
                              onCommit={(next) => commitCopyField(idx, "sub", next)}
                            />
                          </div>
                        ) : (
                          <div className="min-h-[280px] whitespace-pre-wrap rounded-md border border-input bg-muted/30 p-2 text-xs">
                            {flattenSlide(source) || "brak"}
                          </div>
                        )}
                        {hasComposedSlides &&
                        (item?.slides || []).some((s: any) => Number(s.index_n) === idx + 1) ? (
                          <div className="mt-2 space-y-1">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-7 w-full px-1.5 text-[10px]"
                              disabled={copyBusy || !selectedId || syncSlideCopyM.isPending}
                              onClick={() => syncSlideCopyM.mutate(idx + 1)}
                            >
                              {syncSlideCopyM.isPending && syncSlideCopyM.variables === idx + 1 ? (
                                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                              ) : null}
                              Na PNG
                            </Button>
                            {syncSlideCopyM.isError && syncSlideCopyM.variables === idx + 1 ? (
                              <p className="text-[10px] text-destructive">
                                {(syncSlideCopyM.error as Error)?.message || "Błąd"}
                              </p>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
          <CopyChatPanel
            messages={chatMessages}
            pendingActions={pendingActions}
            itemId={item?.id || selectedId}
            busy={Boolean(chatBusy)}
            canUndo={canUndoChat}
            disabled={!item || !selectedId || copyBusy}
            llmProgress={item?.llm_progress}
            llmStatus={item?.llm_status}
            error={
              (copyChatM?.isError && (copyChatM.error as Error)?.message) ||
              (copyReviewOnDemandM?.isError && (copyReviewOnDemandM.error as Error)?.message) ||
              (copyChatUndoM?.isError && (copyChatUndoM.error as Error)?.message) ||
              (copyChatApplyM?.isError && (copyChatApplyM.error as Error)?.message) ||
              (copyChatDismissM?.isError && (copyChatDismissM.error as Error)?.message) ||
              ""
            }
            onSend={(message) => copyChatM.mutate(message)}
            onReviewRoles={() => copyReviewOnDemandM.mutate()}
            onUndo={() => copyChatUndoM.mutate()}
            onApplyPending={(ids) => copyChatApplyM.mutate(ids)}
            onDismissPending={(actionId) => copyChatDismissM.mutate(actionId ? [actionId] : null)}
            applyPending={copyChatApplyM?.isPending}
            dismissPending={copyChatDismissM?.isPending}
            reviewPending={copyReviewOnDemandM?.isPending}
            undoPending={copyChatUndoM?.isPending}
          />
        </div>
      ) : null}
    </TabsContent>
  );
}
